import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../api/apiService';
import type { Training } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ArrowLeft, Clock } from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';
import './TrainingDetail.css';

export const TrainingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [training, setTraining] = useState<Training | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (id) {
      loadTraining(parseInt(id));
    }
  }, [id]);

  const loadTraining = async (trainingId: number) => {
    try {
      const data = await apiService.getTraining(trainingId);
      setTraining(data);
    } catch (error) {
      console.error('Error loading training:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="text-center p-4">{t('loading')}</div>;
  }

  if (!training) {
    return (
      <div className="text-center p-4">
        <p>Entrenamiento no encontrado</p>
        <Button onClick={() => navigate(-1)} className="mt-4">
          {t('goBack')}
        </Button>
      </div>
    );
  }

  return (
    <div className="training-detail-page">
      <Button onClick={() => navigate(-1)} variant="secondary" className="back-button">
        <ArrowLeft size={20} />
        {t('back')}
      </Button>

      <h1 className="page-title">{training.title}</h1>

      <Card className="training-info">
        <div className="info-row">
          <span className="info-label">{t('date')}:</span>
          <span className="info-value">{formatDate(training.date, true)}</span>
        </div>
        {training.observations && (
          <div className="info-row">
            <span className="info-label">{t('observations')}:</span>
            <span className="info-value">{training.observations}</span>
          </div>
        )}
      </Card>

      {/* Warmup Section */}
      {training.warmup_section && training.warmup_section.exercises.length > 0 && (
        <Card className="section-block warmup-block">
          <div className="section-block-header">
            <h2 className="section-block-title">{t('warmup')}</h2>
            <span className="section-count">{training.warmup_section.exercises.length} {t('exercises').toLowerCase()}</span>
          </div>
          <div className="exercises-compact-list">
            {training.warmup_section.exercises.map((exercise, index) => (
              <div key={exercise.id} className="exercise-compact-item">
                <span className="exercise-compact-number">{index + 1}</span>
                <div className="exercise-compact-content">
                  <div className="exercise-compact-header">
                    <span className="exercise-compact-name">{exercise.name}</span>
                    <span className="exercise-compact-category">{exercise.category.name}</span>
                  </div>
                  {exercise.description && (
                    <p className="exercise-compact-description">{exercise.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Main Section */}
      {training.main_section && training.main_section.exercises.length > 0 && (
        <Card className="section-block main-block">
          <div className="section-block-header">
            <h2 className="section-block-title">{t('mainPart')}</h2>
            <span className="section-count">{training.main_section.exercises.length} {t('exercises').toLowerCase()}</span>
          </div>

          {/* Global parameters for main section */}
          <div className="main-params">
            <div className="param-item">
              <span className="param-label">{t('series')}:</span>
              <span className="param-value">{training.main_section.params.series || 0}</span>
            </div>
            <div className="param-item">
              <span className="param-label">{t('seconds')}:</span>
              <span className="param-value">{training.main_section.params.seconds || 0}s</span>
            </div>
            <div className="param-item">
              <span className="param-label">{t('restBetweenSeries')}:</span>
              <span className="param-value">
                <Clock size={16} /> {training.main_section.params.rest_between_series || 0}s
              </span>
            </div>
            <div className="param-item">
              <span className="param-label">{t('restBetweenExercises')}:</span>
              <span className="param-value">
                <Clock size={16} /> {training.main_section.params.rest_between_exercises || 0}s
              </span>
            </div>
          </div>

          <div className="exercises-compact-list">
            {training.main_section.exercises.map((exercise, index) => (
              <div key={exercise.id} className="exercise-compact-item">
                <span className="exercise-compact-number">{index + 1}</span>
                <div className="exercise-compact-content">
                  <div className="exercise-compact-header">
                    <span className="exercise-compact-name">{exercise.name}</span>
                    <span className="exercise-compact-category">{exercise.category.name}</span>
                  </div>
                  {exercise.description && (
                    <p className="exercise-compact-description">{exercise.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Cooldown Section */}
      {training.cooldown_section && training.cooldown_section.exercises.length > 0 && (
        <Card className="section-block cooldown-block">
          <div className="section-block-header">
            <h2 className="section-block-title">{t('cooldown')}</h2>
            <span className="section-count">{training.cooldown_section.exercises.length} {t('exercises').toLowerCase()}</span>
          </div>
          <div className="exercises-compact-list">
            {training.cooldown_section.exercises.map((exercise, index) => (
              <div key={exercise.id} className="exercise-compact-item">
                <span className="exercise-compact-number">{index + 1}</span>
                <div className="exercise-compact-content">
                  <div className="exercise-compact-header">
                    <span className="exercise-compact-name">{exercise.name}</span>
                    <span className="exercise-compact-category">{exercise.category.name}</span>
                  </div>
                  {exercise.description && (
                    <p className="exercise-compact-description">{exercise.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
